import ChevronRight from "@mui/icons-material/ChevronRight";
import Home from "@mui/icons-material/Home";
import Refresh from "@mui/icons-material/Refresh";
import { Box, Breadcrumbs, ButtonBase, Chip, Link, Stack, Typography } from "@mui/material";

import type { LoopStackEntry } from "./loopTypes";

function formatLoopBreadcrumbLabel(entry: LoopStackEntry): string {
	const typeWord = entry.loopType === "table" ? "Table" : "Variable";
	return `${entry.loopName} (${typeWord})`;
}

interface LoopBreadcrumbProps {
  loopStack: LoopStackEntry[];
  /** Navigate to the item at this 0-based index. Pass -1 to go to the main canvas root. */
  onNavigate: (index: number) => void;
}

const LoopBreadcrumb = ({ loopStack, onNavigate }: LoopBreadcrumbProps) => {
	return (
		<Box
			sx={{
				px: 2,
				py: 1,
				borderBottom: "1px solid",
				borderColor: "divider",
				bgcolor: "background.paper",
				display: "flex",
				alignItems: "center",
				gap: 1,
				flexShrink: 0,
				boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
			}}
		>
			{/* Nested loop indicator */}
			<Chip
				label="Loop Canvas"
				size="small"
				icon={<Refresh sx={{ fontSize: "14px !important" }} />}
				sx={{
					bgcolor: "#01D5AB22",
					color: "#01D5AB",
					fontWeight: 700,
					fontSize: "0.7rem",
					height: 24,
					"& .MuiChip-icon": { color: "#01D5AB" },
				}}
			/>

			<Breadcrumbs
				separator={<ChevronRight sx={{ fontSize: 16, color: "text.disabled" }} />}
				sx={{ flex: 1, "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap", alignItems: "center" } }}
			>
				{/* Root: main canvas */}
				<ButtonBase
					onClick={() => onNavigate(-1)}
					sx={{
						borderRadius: 1,
						px: 0.75,
						py: 0.25,
						color: "text.secondary",
						"&:hover": { bgcolor: "action.hover", color: "text.primary" },
						transition: "background-color 0.15s",
					}}
				>
					<Stack direction="row" spacing={0.5} style={{ alignItems: "center" }}>
						<Home sx={{ fontSize: 14 }} />
						<Typography variant="caption" style={{ fontWeight: 600 }}>
              Main Canvas
						</Typography>
					</Stack>
				</ButtonBase>

				{/* Each loop level */}
				{loopStack.map((entry, index) => {
					const isLast = index === loopStack.length - 1;
					return isLast ? (
						<Stack
							key={entry.loopNodeId}
							direction="row"
							spacing={0.5}
							sx={{ px: 0.75, py: 0.25, alignItems: "center" }}
						>
							<Refresh sx={{ fontSize: 14, color: "#01D5AB" }} />
							<Typography variant="caption" style={{ fontWeight: 700, color: "#01D5AB" }}>
								{formatLoopBreadcrumbLabel(entry)}
							</Typography>
						</Stack>
					) : (
						<ButtonBase
							key={entry.loopNodeId}
							onClick={() => onNavigate(index)}
							sx={{
								borderRadius: 1,
								px: 0.75,
								py: 0.25,
								color: "text.secondary",
								"&:hover": { bgcolor: "action.hover", color: "#01D5AB" },
								transition: "background-color 0.15s",
							}}
						>
							<Stack direction="row" spacing={0.5} style={{ alignItems: "center" }}>
								<Refresh sx={{ fontSize: 14 }} />
								<Typography variant="caption" style={{ fontWeight: 600 }}>
									{formatLoopBreadcrumbLabel(entry)}
								</Typography>
							</Stack>
						</ButtonBase>
					);
				})}
			</Breadcrumbs>

			{/* Depth indicator for deeply nested loops */}
			{loopStack.length > 1 && (
				<Chip
					label={`Depth ${loopStack.length}`}
					size="small"
					sx={{
						height: 20,
						fontSize: "0.65rem",
						bgcolor: "action.selected",
						color: "text.secondary",
						fontWeight: 600,
					}}
				/>
			)}
		</Box>
	);
};

export default LoopBreadcrumb;
